import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MessageSquare, BarChart3, TrendingUp, Search, 
  ChevronLeft, ChevronRight, Loader2, Shield, ArrowLeft,
  Sparkles, RefreshCw, Eye, Trash2, X, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin secret - in production this should be handled via proper auth
const ADMIN_SECRET = 'synapsea-admin-2025';

const AI_CONFIG = {
  gpt: { name: 'GPT-4o', color: '#10B981' },
  claude: { name: 'Claude', color: '#F97316' },
  gemini: { name: 'Gemini', color: '#3B82F6' }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState(null);
  const [convPage, setConvPage] = useState(1);
  const [convPagination, setConvPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  const authenticateAdmin = async (e) => {
    e.preventDefault();
    if (!adminKey.trim()) {
      toast.error('Veuillez entrer la clé admin');
      return;
    }
    
    setLoading(true);
    try {
      // Test the admin key by fetching stats
      const response = await axios.get(`${API}/api/admin/stats`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      if (response.data) {
        setIsAuthenticated(true);
        setStats(response.data);
        localStorage.setItem('synapsea_admin_key', adminKey);
        toast.success('Authentification réussie');
      }
    } catch (error) {
      toast.error('Clé admin invalide');
      setAdminKey('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for stored admin key
    const storedKey = localStorage.getItem('synapsea_admin_key');
    if (storedKey) {
      setAdminKey(storedKey);
      // Verify the key is still valid
      axios.get(`${API}/api/admin/stats`, {
        headers: { 'X-Admin-Key': storedKey }
      }).then(response => {
        setIsAuthenticated(true);
        setStats(response.data);
      }).catch(() => {
        localStorage.removeItem('synapsea_admin_key');
      });
    }
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/stats`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/users`, {
        headers: { 'X-Admin-Key': adminKey },
        params: { page, limit: 10, search }
      });
      setUsers(response.data.users);
      setUserPagination(response.data.pagination);
      setUserPage(page);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/conversations`, {
        headers: { 'X-Admin-Key': adminKey },
        params: { page, limit: 10 }
      });
      setConversations(response.data.conversations);
      setConvPagination(response.data.pagination);
      setConvPage(page);
    } catch (error) {
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    setUserDetailLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/users/${userId}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setSelectedUser(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du détail utilisateur');
    } finally {
      setUserDetailLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur et toutes ses données ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success('Utilisateur supprimé');
      fetchUsers(userPage, searchQuery);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'users') {
      fetchUsers(1, searchQuery);
    } else if (isAuthenticated && activeTab === 'conversations') {
      fetchConversations(1);
    }
  }, [activeTab, isAuthenticated]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1, searchQuery);
  };

  const logout = () => {
    localStorage.removeItem('synapsea_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
    setStats(null);
    toast.info('Déconnexion admin');
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-6" data-testid="admin-login-page">
        {/* Background */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[150px]" />
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="font-outfit font-bold text-2xl text-white">Admin Synapsea</h1>
            </div>

            <form onSubmit={authenticateAdmin} className="space-y-6">
              <div>
                <label className="label text-white/50 mb-2 block">Clé d'administration</label>
                <input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Entrez la clé admin..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 outline-none focus:border-purple-500/50 transition-colors"
                  data-testid="admin-key-input"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full btn-glow text-white font-semibold py-3 rounded-xl disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)' }}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Vérification...</>
                ) : (
                  <><Shield className="w-5 h-5 inline mr-2" />Accéder au Back-Office</>
                )}
              </motion.button>
            </form>

            <button 
              onClick={() => navigate('/')} 
              className="mt-6 w-full text-center text-white/40 hover:text-white/70 text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="admin-dashboard-page">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-pink-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-white">Admin Synapsea</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchStats} className="btn-secondary text-sm" data-testid="refresh-stats-btn">
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
            <button onClick={logout} className="btn-secondary text-sm text-red-400 border-red-400/30 hover:border-red-400/50">
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="fixed top-[72px] left-0 right-0 z-40 px-6 py-3 bg-[#05050A]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto flex gap-2">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
            { id: 'users', label: 'Utilisateurs', icon: Users },
            { id: 'conversations', label: 'Conversations', icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                activeTab === tab.id 
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 pt-36 pb-12 px-6 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                  title="Utilisateurs"
                  value={stats.users.total}
                  subValue={`+${stats.users.today} aujourd'hui`}
                  icon={Users}
                  color="#A855F7"
                  testId="kpi-users"
                />
                <KPICard
                  title="Requêtes totales"
                  value={stats.conversations.total}
                  subValue={`+${stats.conversations.today} aujourd'hui`}
                  icon={MessageSquare}
                  color="#10B981"
                  testId="kpi-conversations"
                />
                <KPICard
                  title="Messages IA"
                  value={stats.messages.total}
                  subValue="Réponses générées"
                  icon={Sparkles}
                  color="#3B82F6"
                  testId="kpi-messages"
                />
                <KPICard
                  title="Cette semaine"
                  value={stats.users.this_week}
                  subValue="Nouveaux utilisateurs"
                  icon={TrendingUp}
                  color="#F97316"
                  testId="kpi-weekly"
                />
              </div>

              {/* AI Usage Stats */}
              <div className="glass-card rounded-2xl p-6 mb-8">
                <h3 className="font-outfit font-bold text-lg text-white mb-6">Utilisation par IA</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(AI_CONFIG).map(([key, config]) => {
                    const count = stats.ai_usage[key] || 0;
                    const total = Object.values(stats.ai_usage).reduce((a, b) => a + b, 0) || 1;
                    const percentage = Math.round((count / total) * 100);
                    
                    return (
                      <div key={key} className="text-center" data-testid={`ai-usage-${key}`}>
                        <div 
                          className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3"
                          style={{ backgroundColor: `${config.color}20`, border: `1px solid ${config.color}40` }}
                        >
                          <span className="font-mono font-bold text-lg" style={{ color: config.color }}>
                            {percentage}%
                          </span>
                        </div>
                        <div className="font-medium text-white text-sm">{config.name}</div>
                        <div className="text-xs text-white/40">{count} requêtes</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Registrations Chart */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="font-outfit font-bold text-lg text-white mb-6 flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-400" />
                    Inscriptions (30 derniers jours)
                  </h3>
                  <SimpleBarChart data={stats.charts.daily_registrations} color="#A855F7" />
                </div>

                {/* Daily Queries Chart */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="font-outfit font-bold text-lg text-white mb-6 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-emerald-400" />
                    Requêtes (30 derniers jours)
                  </h3>
                  <SimpleBarChart data={stats.charts.daily_queries} color="#10B981" />
                </div>
              </div>
            </motion.div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <motion.div
              key="users"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Search */}
              <form onSubmit={handleSearch} className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher par nom ou email..."
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 outline-none focus:border-purple-500/50"
                    data-testid="user-search-input"
                  />
                </div>
                <button type="submit" className="btn-primary" data-testid="user-search-btn">
                  Rechercher
                </button>
              </form>

              {/* Users Table */}
              <div className="glass-card rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Utilisateur</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Email</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Conversations</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Inscrit le</th>
                      <th className="px-6 py-4 text-right text-xs font-mono uppercase text-white/40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto" />
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-white/40">
                          Aucun utilisateur trouvé
                        </td>
                      </tr>
                    ) : (
                      users.map(user => (
                        <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${user.user_id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold">
                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <span className="text-white font-medium">{user.name || 'Sans nom'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-white/60 text-sm">{user.email}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-sm">
                              {user.conversations_count || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-white/40 text-sm">
                            {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => fetchUserDetail(user.user_id)}
                                className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-white"
                                data-testid={`view-user-${user.user_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteUser(user.user_id)}
                                className="p-2 rounded-lg hover:bg-red-500/20 text-white/50 hover:text-red-400"
                                data-testid={`delete-user-${user.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {userPagination && userPagination.pages > 1 && (
                  <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-sm text-white/40">
                      Page {userPagination.page} sur {userPagination.pages} ({userPagination.total} utilisateurs)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchUsers(userPage - 1, searchQuery)}
                        disabled={userPage === 1}
                        className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5 text-white" />
                      </button>
                      <button
                        onClick={() => fetchUsers(userPage + 1, searchQuery)}
                        disabled={userPage === userPagination.pages}
                        className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Conversations Tab */}
          {activeTab === 'conversations' && (
            <motion.div
              key="conversations"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="glass-card rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Question</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Utilisateur</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">IA utilisées</th>
                      <th className="px-6 py-4 text-left text-xs font-mono uppercase text-white/40">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto" />
                        </td>
                      </tr>
                    ) : conversations.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-white/40">
                          Aucune conversation
                        </td>
                      </tr>
                    ) : (
                      conversations.map(conv => (
                        <tr key={conv.conversation_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`conv-row-${conv.conversation_id}`}>
                          <td className="px-6 py-4">
                            <p className="text-white text-sm line-clamp-2 max-w-md">{conv.question}</p>
                          </td>
                          <td className="px-6 py-4 text-white/60 text-sm">
                            {conv.user?.name || conv.user?.email || 'Invité'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-1">
                              {conv.selected_ais?.map(ai => (
                                <div
                                  key={ai}
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: AI_CONFIG[ai]?.color || '#666' }}
                                  title={AI_CONFIG[ai]?.name || ai}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-white/40 text-sm">
                            {new Date(conv.created_at).toLocaleString('fr-FR')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {convPagination && convPagination.pages > 1 && (
                  <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                    <span className="text-sm text-white/40">
                      Page {convPagination.page} sur {convPagination.pages} ({convPagination.total} conversations)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => fetchConversations(convPage - 1)}
                        disabled={convPage === 1}
                        className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-5 h-5 text-white" />
                      </button>
                      <button
                        onClick={() => fetchConversations(convPage + 1)}
                        disabled={convPage === convPagination.pages}
                        className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* User Detail Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedUser(null)}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative w-full max-w-2xl max-h-[80vh] glass rounded-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {userDetailLoading ? (
                <div className="p-12 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : (
                <>
                  <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                        {selectedUser.user?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <h2 className="font-outfit font-bold text-xl text-white">{selectedUser.user?.name}</h2>
                        <p className="text-white/50 text-sm">{selectedUser.user?.email}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedUser(null)} className="p-2 rounded-full hover:bg-white/10">
                      <X className="w-5 h-5 text-white/50" />
                    </button>
                  </div>
                  <div className="p-6 overflow-y-auto max-h-[60vh]">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="glass-card rounded-xl p-4">
                        <div className="text-2xl font-bold text-white">{selectedUser.stats?.total_conversations || 0}</div>
                        <div className="text-xs text-white/40">Conversations</div>
                      </div>
                      <div className="glass-card rounded-xl p-4">
                        <div className="text-sm text-white/70 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(selectedUser.user?.created_at).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="text-xs text-white/40">Date d'inscription</div>
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-white mb-4">Dernières conversations</h3>
                    <div className="space-y-3">
                      {selectedUser.conversations?.slice(0, 5).map(conv => (
                        <div key={conv.conversation_id} className="glass-card rounded-xl p-4">
                          <p className="text-white/80 text-sm line-clamp-2 mb-2">{conv.question}</p>
                          <div className="flex items-center gap-2">
                            {conv.selected_ais?.map(ai => (
                              <div
                                key={ai}
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: AI_CONFIG[ai]?.color }}
                              />
                            ))}
                            <span className="text-xs text-white/30 ml-2">
                              {new Date(conv.created_at).toLocaleString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// KPI Card Component
function KPICard({ title, value, subValue, icon: Icon, color, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-2xl p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
      <div className="text-3xl font-outfit font-bold text-white mb-1">{value.toLocaleString()}</div>
      <div className="text-sm text-white/50">{title}</div>
      <div className="text-xs mt-2" style={{ color }}>{subValue}</div>
    </motion.div>
  );
}

// Simple Bar Chart Component
function SimpleBarChart({ data, color }) {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  const last7Days = data.slice(-7);
  
  return (
    <div className="flex items-end gap-2 h-32">
      {last7Days.map((item, index) => {
        const height = (item.count / maxValue) * 100;
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-2">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${Math.max(height, 4)}%` }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="w-full rounded-t-lg"
              style={{ backgroundColor: color, minHeight: '4px' }}
            />
            <span className="text-[10px] text-white/30">
              {new Date(item.date).toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
